import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import Drawer from "./Drawer";
import { LoaderSpinner } from "./LoaderSpinner";
import { ConfrimModal } from "./ConfrimModal";

const formatChangedAt = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

/**
 * Shared presentational change-history table used by the SKU Allocation and
 * Product Catalog history drawers. Containers own fetching, formatting, and
 * row-level actions; this component only renders.
 */
export const ChangeHistoryDrawer = ({
    isOpen,
    onClose,
    title,
    description,
    width = "800px",
    marginFromSidebar,
    mobileTopOffset,
    searchTerm,
    onSearchTermChange,
    searchPlaceholder = "Filter by SKU…",
    currentPage = 1,
    totalPages,
    isTheLastPage,
    onPageChange,
    totalCountLabel,
    loading,
    emptyText = "No changes logged yet.",
    rows = [],
    pendingAction,
    onCancelAction,
    onConfirmAction,
    actionInFlight,
}) => (
    <>
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            width={width}
            marginFromSidebar={marginFromSidebar}
            mobileTopOffset={mobileTopOffset}
            showActionButtons={false}
            headerContent={
                <div className="flex items-center justify-between gap-3 pr-2">
                    <h2 className="text-xl font-bold text-gray-900">
                        {title}
                    </h2>
                    {totalPages == null ? (
                        totalCountLabel ? (
                            <div className="shrink-0 text-xs text-gray-500">{totalCountLabel}</div>
                        ) : null
                    ) : (
                        <div className="flex shrink-0 items-center gap-1">
                            {totalCountLabel ? (
                                <span className="mr-2 text-xs text-gray-500 whitespace-nowrap">{totalCountLabel}</span>
                            ) : null}
                            <button
                                type="button"
                                disabled={currentPage <= 1 || loading}
                                onClick={() => onPageChange?.(Math.max(currentPage - 1, 1))}
                                className="rounded-md bg-white p-1.5 text-gray-700 transition-all duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <ChevronLeftIcon className="h-3 w-3" />
                            </button>
                            <p className="text-xs text-gray-500 whitespace-nowrap text-center min-w-[3rem]">
                                {`${currentPage}/${totalPages}`}
                            </p>
                            <button
                                type="button"
                                disabled={isTheLastPage || loading}
                                onClick={() => onPageChange?.(currentPage + 1)}
                                className="rounded-md bg-white p-1.5 text-gray-700 transition-all duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <ChevronRightIcon className="h-3 w-3" />
                            </button>
                        </div>
                    )}
                </div>
            }
        >
            <div className="flex flex-col gap-3 px-4 pb-6">
                {description ? (
                    <p className="text-sm text-gray-500 mt-4">
                        {description}
                    </p>
                ) : null}
                {searchTerm !== undefined ? (
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => onSearchTermChange?.(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                    />
                ) : null}
                {loading ? (
                    <div className="flex justify-center py-10">
                        <LoaderSpinner width="48" height="48" border="4" colorSpinner="gray" />
                    </div>
                ) : rows.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-500">
                        {emptyText}
                    </p>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="px-3 py-2 font-medium">When</th>
                                    <th className="px-3 py-2 font-medium">SKU</th>
                                    <th className="px-3 py-2 font-medium">Field</th>
                                    <th className="px-3 py-2 font-medium">Change</th>
                                    <th className="px-3 py-2 font-medium">Source</th>
                                    <th className="px-3 py-2 font-medium">By</th>
                                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                                        <td className="whitespace-nowrap px-3 py-2 align-top text-gray-500">
                                            {formatChangedAt(row.when)}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 align-top font-medium text-gray-800">
                                            {row.subject}
                                            {row.subjectSubtitle ? (
                                                <div className="whitespace-normal break-words font-normal text-gray-500">
                                                    {row.subjectSubtitle}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td className="px-3 py-2 align-top break-words text-gray-700">
                                            {(row.fields || []).map((field, index) => (
                                                <div key={`${row.id}-field-${index}`} className="break-words">{field}</div>
                                            ))}
                                        </td>
                                        <td className="px-3 py-2 align-top break-words text-gray-700">
                                            {(row.changes || []).map((change) => (
                                                <div key={`${row.id}-change-${change.key}`} className="break-words">
                                                    {change.oldValue}
                                                    <span className="mx-1 text-gray-400">→</span>
                                                    {change.newValue}
                                                </div>
                                            ))}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 align-top">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${row.source?.tone || ""}`}
                                            >
                                                {row.source?.label}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 align-top text-gray-500">
                                            {row.by || "—"}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 align-top text-right">
                                            <div className="flex justify-end gap-1.5">
                                                {(row.actions || []).map((action) => (
                                                    <button
                                                        key={`${row.id}-action-${action.key}`}
                                                        type="button"
                                                        className="rounded-md border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                        disabled={action.disabled}
                                                        onClick={action.onClick}
                                                    >
                                                        {action.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Drawer>
        <ConfrimModal
            isOpen={Boolean(pendingAction)}
            isLoading={actionInFlight}
            title={pendingAction?.label || "Revert changes?"}
            confirmButtonTitle="Yes, revert"
            cancelClick={() => {
                if (actionInFlight) return;
                onCancelAction?.();
            }}
            submitClick={onConfirmAction}
            size="max-w-lg"
        />
    </>
);

export default ChangeHistoryDrawer;
